/**
 * clientErrors.js — a crash in the browser is sent to pidly, with everything
 * a person reading it later would need, and shows up on the Admin page.
 *
 *   reportClientError({ error, componentStack, source })
 *     one report; the same message is not sent again within a minute.
 *   installGlobalErrorReporting()
 *     window 'error' and 'unhandledrejection' feed the same reporter, so
 *     crashes outside React's tree (event handlers, promises) are seen too.
 *
 * Never throws, never awaits anything the caller needs: reporting a crash
 * must not be able to cause one.
 */
import { BACKEND_URL } from './config.js';
import { getAuthToken, getStoredUser } from './authStorage';

const sentAt = new Map(); // message → time
const DEDUPE_MS = 60_000;

function buildId() {
  try { return typeof __BUILD_ID__ !== 'undefined' ? String(__BUILD_ID__) : 'dev'; } catch { return 'dev'; }
}

export function reportClientError({ error, componentStack = null, source = 'boundary', extra = null } = {}) {
  try {
    const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : (error?.message || 'Unknown error'));
    const message = String(err.message || 'Unknown error');
    const now = Date.now();
    const last = sentAt.get(message) || 0;
    if (now - last < DEDUPE_MS) return;
    sentAt.set(message, now);
    if (sentAt.size > 200) sentAt.clear();

    const user = (() => { try { return getStoredUser(); } catch { return null; } })();
    const token = (() => { try { return getAuthToken(); } catch { return null; } })();
    const theme = (() => { try { return document.documentElement.getAttribute('data-theme') || ''; } catch { return ''; } })();
    const mem = (() => { try { const m = performance?.memory; return m ? { usedMB: Math.round(m.usedJSHeapSize / 1048576), limitMB: Math.round(m.jsHeapSizeLimit / 1048576) } : null; } catch { return null; } })();

    const body = {
      source,
      message,
      name: err.name || null,
      stack: err.stack || null,
      componentStack,
      route: `${window.location.pathname}${window.location.search}`,
      buildId: buildId(),
      theme,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      email: user?.email || null,
      extra: {
        online: navigator.onLine,
        language: navigator.language,
        referrer: document.referrer || null,
        memory: mem,
        time: new Date().toISOString(),
        ...(extra && typeof extra === 'object' ? extra : {}),
      },
    };
    fetch(`${BACKEND_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch { /* reporting must never throw */ }
}

// Noise no one can act on: a benign browser observer message, and errors
// from other origins (extensions) that arrive with no detail at all.
const IGNORE = /ResizeObserver loop|^Script error\.?$/i;

// A tab the browser kills (out of memory, "Aw, Snap") runs no script on the
// way out, so nothing above can see it. A heartbeat in sessionStorage can:
// it is written while the page runs and removed on a clean unload, and the
// tab keeps its sessionStorage when it is reloaded after a crash. Finding a
// heartbeat at start-up means the last page in this tab died without
// unloading; it is reported with where it was and how much memory it held.
const ALIVE_KEY = 'pidly.alive';
const heap = () => { try { const m = performance?.memory; return m ? Math.round(m.usedJSHeapSize / 1048576) : null; } catch { return null; } };
function watchForCrashes() {
  try {
    const raw = sessionStorage.getItem(ALIVE_KEY);
    if (raw) {
      const last = JSON.parse(raw);
      reportClientError({
        error: new Error('The tab ended without unloading (browser crash or out of memory)'),
        source: 'crash',
        extra: { lastRoute: last.route || null, lastHeapMB: last.heapMB ?? null, peakHeapMB: last.peakMB ?? null, lastBeat: last.at || null, since: last.since || null },
      });
    }
  } catch { /* storage blocked: no crash watch */ }
  let peak = 0;
  const since = new Date().toISOString();
  const beat = () => {
    try {
      const h = heap();
      if (h != null && h > peak) peak = h;
      sessionStorage.setItem(ALIVE_KEY, JSON.stringify({ route: window.location.pathname, heapMB: h, peakMB: peak || null, at: new Date().toISOString(), since }));
    } catch { /* storage full or blocked */ }
  };
  beat();
  setInterval(beat, 15_000);
  window.addEventListener('pagehide', () => { try { sessionStorage.removeItem(ALIVE_KEY); } catch { /* blocked */ } });
  // Back from the bfcache: the page is alive again.
  window.addEventListener('pageshow', (e) => { if (e.persisted) beat(); });
}

let installed = false;
export function installGlobalErrorReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  watchForCrashes();
  window.addEventListener('error', (e) => {
    const msg = e?.message || e?.error?.message || '';
    if (!msg || IGNORE.test(msg)) return;
    reportClientError({ error: e.error || new Error(msg), source: 'window', extra: { file: e.filename || null, line: e.lineno || null, col: e.colno || null } });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    const msg = r?.message || (typeof r === 'string' ? r : '');
    if (!msg || IGNORE.test(msg)) return;
    // A failed fetch on a flaky connection is not a crash.
    if (/Failed to fetch|NetworkError|Load failed|aborted/i.test(msg)) return;
    reportClientError({ error: r instanceof Error ? r : new Error(msg), source: 'promise' });
  });
}
